import axios from 'axios';
import { config } from '../config';
import SessionService from './SessionService';

const API_URL = config.API_BASE_URL;

const authHeaders = () => ({
  Accept: 'application/json',
  Authorization: `Bearer ${SessionService.getToken()}`,
});

export type DossierSectionStatus = 'ok' | 'partial' | 'unavailable';
export type DossierScope = 'card' | 'contact';

export interface DossierCard {
  id: number;
  sectorId: number;
  title: string | null;
  columnId: number | null;
  columnName: string | null;
  columnKind: string | null;
  isClosed: boolean;
  isEntry: boolean;
  estimatedValue: number | null;
  currency: string | null;
  temperature: string | null;
  createdAt: string;
  createdBy: string | null;
  wonAt: string | null;
  lostAt: string | null;
  daysStalled: number | null;
  isStalled: boolean;
}

export interface DossierPeople {
  responsibleUserId: number | null;
  responsibleName: string | null;
  aiAgentId: number | null;
  aiAgentName: string | null;
  sectorName: string | null;
}

export interface DossierStage {
  columnName: string | null;
  since: string | null;
  evidence: string | null;
  trigger: string | null;
  confidence: string | null;
  actorUserId: number | null;
  actorName: string | null;
}

export interface DossierOpportunityRef {
  id: number;
  title: string | null;
  columnName: string | null;
  columnKind: string | null;
  isClosed: boolean;
}

export interface DossierConversationSection {
  status: DossierSectionStatus;
  lastInboundAt: string | null;
  lastInboundPreview: string | null;
  lastOutboundAt: string | null;
  whatsappMessages: number;
  emailMessages: number;
  lastEmailAt: string | null;
  openPath: string;
}

export interface DossierDocument {
  id: string;
  source: 'whatsapp' | 'email' | 'registrar' | 'proposal';
  direction: 'received' | 'sent' | 'internal';
  fileName: string;
  mimeType: string | null;
  fileKind: string;
  at: string | null;
  url: string | null;
  stored: boolean;
  summary: string | null;
  scope: DossierScope;
  linked: boolean;
  openPath: string;
}

export interface DossierDocumentsSection {
  status: DossierSectionStatus;
  items: DossierDocument[];
  otherOpportunityCount: number;
  truncated: boolean;
  failedSources: string[];
}

export interface DossierCommitment {
  id: string;
  kind: 'commitment' | 'meeting' | 'scheduled_message';
  state: string;
  title: string;
  detail: string | null;
  quote: string | null;
  at: string | null;
  byAi: boolean;
  scope: DossierScope;
  linked: boolean;
  openPath: string;
}

export interface DossierCommitmentsSection {
  status: DossierSectionStatus;
  items: DossierCommitment[];
  otherOpportunityCount: number;
  truncated: boolean;
  failedSources: string[];
}

export interface DossierDecision {
  id: number;
  title: string;
  createdAt: string;
  openPath: string;
}

export interface DossierDecisionsSection {
  status: DossierSectionStatus;
  items: DossierDecision[];
}

export interface DossierNextAction {
  kind: string;
  title: string;
  detail: string | null;
  at: string | null;
  evidenceQuote: string | null;
  evidenceRef: string | null;
  scope: DossierScope;
  byAi: boolean;
  openPath: string | null;
}

export interface DossierNextActionSection {
  status: DossierSectionStatus;
  action: DossierNextAction | null;
}

export interface OpportunityDossier {
  card: DossierCard;
  contact: { id: number; name: string };
  people: DossierPeople;
  stage: DossierStage | null;
  otherOpportunities: DossierOpportunityRef[];
  conversation: DossierConversationSection;
  documents: DossierDocumentsSection;
  commitments: DossierCommitmentsSection;
  decisions: DossierDecisionsSection;
  nextAction: DossierNextActionSection;
  generatedAtUtc: string;
}

export const getOpportunityDossier = async (cardId: number): Promise<OpportunityDossier> => {
  const response = await axios.get(`${API_URL}/cards/${cardId}/dossier`, { headers: authHeaders() });
  return response.data as OpportunityDossier;
};

export const linkDossierItem = async (cardId: number, itemId: string): Promise<void> => {
  await axios.put(`${API_URL}/cards/${cardId}/dossier/links`, { itemId }, { headers: authHeaders() });
};

export const unlinkDossierItem = async (cardId: number, itemId: string): Promise<void> => {
  await axios.delete(`${API_URL}/cards/${cardId}/dossier/links`, { headers: authHeaders(), params: { itemId } });
};

export const dossierErrorKind = (e: unknown): 'not_found' | 'forbidden' | 'other' => {
  const status = (e as { response?: { status?: number } } | null)?.response?.status;
  if (status === 404) return 'not_found';
  if (status === 403) return 'forbidden';
  return 'other';
};
